import { Banner, TextLink, Typography } from '@neo4j-ndl/react';
import { ExclamationCircleIconOutline } from '@neo4j-ndl/react/icons';
import { AURA_CONSOLE_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import type { Instance, InstanceSize, Project, TierConfig } from '@nx/state';
import { PLAN_TYPE, TIER, isMarketplaceProject, useTrackEvent } from '@nx/state';
import { Objects, type Validation, isNotNullish, isNullish } from '@nx/stdlib';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';

import { formatCost, getCurrencyForBillingMethod } from '../../utils';
import { getValidSize, getVisibleSizes, isPAYG } from '../entities/helpers';
import type { InstanceFormData, Product } from '../entities/model';
import { AdditionalSettingsSection, CspSection, DetailsSection, TierCard } from '../form-components';
import { GraphAnalyticsSection } from '../form-components/graph-analytics-section';
import { SizeSection } from '../form-components/size-section';
import { getValidGdsPluginValue, getValidVectorOptimizedValue } from './form-additional-settings';
import { getValidRegion } from './helper';

export interface NewBaseCreateInstanceFormProps {
  data: InstanceFormData;
  onDataChange: (data: Partial<InstanceFormData>) => void;
  onSelectTier: (tier: TIER) => void;
  tierConfig: TierConfig;
  validation: Validation<InstanceFormData> | null;
  project: Project;
  instance?: Instance;
  product: Product;
  onShowProTrialOptions?: (checked: boolean) => void;
  showProTrialOptions?: boolean;
  showFreeTier?: boolean;
}

export const NewBaseInstanceForm = ({
  data,
  onDataChange,
  tierConfig,
  validation,
  project,
  instance,
  onSelectTier,
  product,
  onShowProTrialOptions,
  showProTrialOptions,
  showFreeTier = true,
}: NewBaseCreateInstanceFormProps) => {
  const projectCurrency = getCurrencyForBillingMethod(project.billingMethod);
  const trackEvent = useTrackEvent();

  const availableSizes = useMemo(() => {
    let { sizes } = tierConfig;
    if (isNotNullish(data.cloudProvider)) {
      sizes = sizes.filter((size) => size.cloudProvider === data.cloudProvider);
    }

    return sizes.filter((size) => size.isTrial === (showProTrialOptions === true && data.tier === TIER.PROFESSIONAL));
  }, [data.cloudProvider, tierConfig, showProTrialOptions, data.tier]);

  const resetDataIfNeeded = useCallback(
    (newData: InstanceFormData, isProTrial: boolean) => {
      const newVisibleSizes = getVisibleSizes(newData, tierConfig, isProTrial);

      const newRegion = getValidRegion(tierConfig.cloudProviderRegions, newData.cloudProvider, newData.region);

      const newSize = getValidSize({ ...newData, region: newRegion }, project, newVisibleSizes, {
        isTrial: isProTrial,
        ...(isNotNullish(newData.size) && {
          defaultSize: {
            memory: newData.size.memory,
            storage: newData.size.storage,
          },
        }),
      });
      const newerData = { ...newData, region: newRegion, size: newSize };

      const resetData = {
        ...newData,
        region: newRegion,
        size: newSize,
        gdsPlugin: getValidGdsPluginValue(newerData),
        vectorOptimized: getValidVectorOptimizedValue(newerData),
        confirmed: [newSize, newData.region].some(isNullish) ? false : newData.confirmed,
      };

      return resetData;
    },
    [project, tierConfig],
  );

  const handleDataChange = useCallback(
    (changedData: Partial<InstanceFormData>, isProTrial: boolean = showProTrialOptions ?? false) => {
      const newData = resetDataIfNeeded({ ...data, ...changedData }, isProTrial);
      onDataChange(newData);
    },
    [resetDataIfNeeded, onDataChange, showProTrialOptions, data],
  );
  const willNotInheritFiltering = useMemo(() => {
    if (isNotNullish(project.ipFilterId) && [TIER.FREE, TIER.PROFESSIONAL].includes(data.tier)) {
      return true;
    }
    return false;
  }, [data, project]);

  const handleSizeChange = useCallback(
    (size: InstanceSize | undefined) => {
      handleDataChange({ ...data, size });
    },
    [data, handleDataChange],
  );

  const handleShowProTrialOptionsChange = (checked: boolean) => {
    const event = checked
      ? AURA_CONSOLE_EVENTS.INSTANCE_PRO_TRIAL_TOGGLE
      : AURA_CONSOLE_EVENTS.INSTANCE_PRO_TRIAL_UNTOGGLE;
    trackEvent({ event, scope: APP_SCOPE.aura });
    onShowProTrialOptions?.(checked);
    handleDataChange(data, checked);
  };

  const handleTierChange = (tier: TIER) => {
    onSelectTier(tier);
  };

  const availableTiers = Objects.keys(project.tierConfigs);
  const isMarketplace = isMarketplaceProject(project.projectType);

  return (
    <div className="@container flex flex-col gap-4">
      {project.planType === PLAN_TYPE.SELF_SERVE && (
        <>
          <div
            className={classNames('grid grid-cols-1 gap-4', {
              '@sm:grid-cols-3': product === 'AuraDB' && showFreeTier,
              '@sm:grid-cols-2': product === 'AuraDS' || !showFreeTier,
            })}
          >
            <>
              {product === 'AuraDB' ? (
                <>
                  <TierCard
                    data-testid="auradb-pro-card"
                    title="Professional"
                    description="Build production-ready applications"
                    items={[
                      'Database monitoring with logs and metrics',
                      'Predefined RBAC',
                      'Daily backups, 7d retention',
                      'Graph algorithms, vector optimized',
                      'Standard support',
                    ]}
                    footer={
                      <div className="text-neutral-text-default flex gap-1">
                        <div className="text-neutral-text-weak">From</div>
                        <div>$0.09</div>
                        <div>/GB/hour</div>
                      </div>
                    }
                    isSelected={data.tier === TIER.PROFESSIONAL}
                    isTrialAvailable={project.availableActions.create_pro_trial.enabled}
                    onSelect={() => {
                      handleTierChange(TIER.PROFESSIONAL);
                    }}
                  />

                  <TierCard
                    data-testid="auradb-mte-card"
                    title="Business Critical"
                    description="Scale enterprise-grade applications"
                    items={[
                      'Advanced monitoring, 3rd party integrations',
                      'Custom RBAC, IP filtering, SSO',
                      'Hourly backups, 30d retention',
                      'Graph algorithms, vector optimized',
                      '24/7 support',
                      'High availability with 99.95% uptime SLA',
                    ]}
                    footer={
                      <>
                        <div className="text-neutral-text-default flex gap-1">
                          <div className="text-neutral-text-weak">From</div>
                          <div>$0.20</div>
                          <div>/GB/hour</div>
                        </div>
                        <div className="text-neutral-text-weak">Min 2GB</div>
                      </>
                    }
                    isDisabled={!availableTiers.includes(TIER.MTE)}
                    disabledReason={{
                      icon: <ExclamationCircleIconOutline className="size-4 shrink-0" />,
                      reason: isMarketplace ? 'Unavailable from Marketplace' : 'Business Critical is not available',
                    }}
                    isSelected={data.tier === TIER.MTE}
                    onSelect={() => {
                      handleTierChange(TIER.MTE);
                    }}
                  />
                  {showFreeTier && (
                    <TierCard
                      data-testid="auradb-free-card"
                      title="Free"
                      description="Learn and explore graphs for free"
                      items={['Limited memory and CPU', 'Up to 200k nodes and 400k relationships', 'Limited backups']}
                      footer="$0 / hour"
                      isSelected={data.tier === TIER.FREE}
                      isDisabled={!project.allowFreeDatabaseCreation || isMarketplace}
                      disabledReason={{
                        icon: <ExclamationCircleIconOutline className="size-4 shrink-0" />,
                        reason: isMarketplace ? 'Unavailable from Marketplace' : 'Maximum free instances reached',
                      }}
                      onSelect={() => {
                        handleTierChange(TIER.FREE);
                      }}
                    />
                  )}
                </>
              ) : (
                <TierCard
                  data-testid="aurads-pro-card"
                  title="AuraDS Professional"
                  description="Take your graph data science experiments to production"
                  items={[
                    'Scalable on demand',
                    'Backups with 7d retention',
                    'Advanced instance-level metrics',
                    'Standard support',
                  ]}
                  footer={
                    <>
                      <div className="flex gap-1">
                        From
                        <div className="text-neutral-text-default">
                          {formatCost(0.125, projectCurrency, { maximumDecimalPlaces: 3, hideAcuCurrency: true })}{' '}
                          /GB/hour
                        </div>
                      </div>
                      {''}
                      Min 8GB
                    </>
                  }
                  isSelected={data.tier === TIER.GDS}
                  onSelect={() => {
                    handleTierChange(TIER.GDS);
                  }}
                />
              )}
            </>
          </div>
          {product === 'AuraDB' && (
            <Typography variant="body-medium" className="text-neutral-text-weak flex flex-wrap gap-1">
              Deploy in a logically isolated cloud environment —
              <TextLink type="external" href="https://neo4j.com/contact-us/?about_me=aura-console-vdc-contact-sales">
                See Virtual Dedicated Cloud
              </TextLink>
            </Typography>
          )}
        </>
      )}
      {willNotInheritFiltering && (
        <Banner type="warning" usage="inline" className="max-w-full" hasIcon>
          <Typography variant="body-large" as="div" className="flex flex-col gap-4">
            The selected tier does not support IP Filtering and therefore will not be filtered by this Project's
            configured filter.
          </Typography>
        </Banner>
      )}
      <DetailsSection data={data} onDataChange={handleDataChange} validation={validation} />
      {data.tier !== TIER.FREE && (
        <>
          <CspSection
            data={data}
            onDataChange={handleDataChange}
            validation={validation}
            tierConfig={tierConfig}
            project={project}
            instance={instance}
          />
          <SizeSection
            project={project}
            data={data}
            options={availableSizes}
            onDataChange={handleSizeChange}
            showTrialSizes={showProTrialOptions}
            onShowTrialSizes={handleShowProTrialOptionsChange}
            hidePricing={!isPAYG(data.tier, project.billingMethod)}
          />
          {[TIER.ENTERPRISE, TIER.MTE, TIER.PROFESSIONAL].includes(data.tier) && (
            <GraphAnalyticsSection data={data} onDataChange={handleDataChange} />
          )}
          <AdditionalSettingsSection data={data} onDataChange={handleDataChange} project={project} />
        </>
      )}
    </div>
  );
};

type NewInstanceFormProps = Omit<NewBaseCreateInstanceFormProps, 'tierConfig'> & {
  tierConfig: TierConfig | undefined;
};

export const NewInstanceForm = ({ tierConfig, ...props }: NewInstanceFormProps) => {
  if (isNullish(tierConfig)) {
    return (
      <Banner
        description="There is something wrong with the configuration of your project. Reload the page and if the error persists contact customer support."
        type="danger"
        className="mt-4"
        usage="inline"
      />
    );
  }

  return <NewBaseInstanceForm tierConfig={tierConfig} {...props} />;
};
